import { useCallback, useRef, useState } from 'react'

import { handleFetchErrorFunctional } from 'Utilities'

import {
  submitPublicReturnLabel,
  type SubmitReturnLabelResponse,
} from './PublicRequestReturnLabel.api'
import type { SubmitReturnLabelPayload } from './PublicRequestReturnLabel.types'

interface UsePublicReturnLabelSubmitResult {
  hasSubmitError: boolean
  isSubmitting: boolean
  submit: (
    payload: SubmitReturnLabelPayload,
  ) => Promise<SubmitReturnLabelResponse | null>
}

export const usePublicReturnLabelSubmit =
  (): UsePublicReturnLabelSubmitResult => {
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [hasSubmitError, setHasSubmitError] = useState(false)
    const submitLockRef = useRef(false)

    const submit = useCallback(
      (
        payload: SubmitReturnLabelPayload,
      ): Promise<SubmitReturnLabelResponse | null> => {
        if (submitLockRef.current) {
          return Promise.resolve(null)
        }

        submitLockRef.current = true
        setIsSubmitting(true)
        // Self-clear stale error state at the start of a fresh attempt so the
        // hook is correct even if the caller forgets to call reset() first.
        setHasSubmitError(false)

        return submitPublicReturnLabel(payload)
          .catch((error: unknown) => {
            const reportedError =
              error instanceof Error ? error : new Error(String(error))

            handleFetchErrorFunctional()(reportedError, {
              operation: 'submitPublicReturnLabel',
            })
            setHasSubmitError(true)

            return null
          })
          .finally(() => {
            submitLockRef.current = false
            setIsSubmitting(false)
          })
      },
      [],
    )

    return { hasSubmitError, isSubmitting, submit }
  }
